import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useSite } from "@/context/SiteContext";

function ShowcaseTile({ c, i }: { c: any; i: number }) {
  return (
    <Link to={`/collections/${c.slug}`} className="group relative block aspect-[4/5] sm:aspect-[5/4] overflow-hidden bg-neutral-900 rise" style={{ animationDelay: `${i * 90}ms` }} data-testid={`showcase-tile-${c.slug}`}>
      {c.image && <img src={c.image} alt={c.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105" />}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 p-6 md:p-10 text-white">
        <p className="eyebrow text-[10px] text-white/75" data-testid={`showcase-count-${c.slug}`}>{c.count ? `${c.count} piece${c.count === 1 ? "" : "s"}` : "Coming soon"}</p>
        <h3 className="font-display font-light uppercase tracking-[0.08em] text-3xl md:text-4xl mt-2">{c.title}</h3>
        {c.intro && <p className="text-sm text-white/85 mt-3 max-w-md leading-relaxed line-clamp-2">{c.intro}</p>}
        <span className="inline-flex items-center gap-2 eyebrow text-[11px] mt-5 border-b border-white/60 pb-1 transition-[gap] duration-300 group-hover:gap-3.5">
          {c.count ? "Shop now" : "Discover"} <ArrowRight size={13} />
        </span>
      </div>
    </Link>
  );
}

export function CollectionShowcase({ b }: any) {
  const { collections } = useSite();
  const items = b.source === "lookbook"
    ? (collections || []).filter((c: any) => c.group === "lookbook").map((c: any) => ({ slug: c.slug }))
    : (b.items || []).filter((x: any) => x.slug);
  const key = items.map((x: any) => x.slug).join(",");
  const [data, setData] = useState<Record<string, any>>({});
  useEffect(() => {
    if (key) api.get("/collections/summary", { params: { slugs: key } }).then((r) => setData(Object.fromEntries(r.data.map((c: any) => [c.slug, c])))).catch(() => {});
  }, [key]);
  const tiles = items.map((x: any) => data[x.slug] && { ...data[x.slug], title: x.label || data[x.slug].title, image: x.image || data[x.slug].image, intro: x.text || data[x.slug].intro }).filter(Boolean);
  return (
    <section className="max-w-[1400px] mx-auto px-5 md:px-12 py-12 md:py-20" data-testid="collection-showcase">
      {b.heading && <h2 className="font-display uppercase tracking-[0.08em] text-3xl md:text-4xl text-center mb-4">{b.heading}</h2>}
      {b.intro && <p className="text-center text-neutral-600 max-w-2xl mx-auto mb-12">{b.intro}</p>}
      <div className="grid sm:grid-cols-2 gap-5 md:gap-8">
        {tiles.map((c: any, i: number) => <ShowcaseTile key={c.slug} c={c} i={i} />)}
      </div>
    </section>
  );
}
